import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { dictionaries } from "../i18n";
import { SERVICES } from "../data/services";
import { POSTS } from "../data/posts";
import { COMPANY } from "../data/config";
import { track } from "../lib/track";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SiteContext = createContext(null);
export const useSite = () => useContext(SiteContext);
export const useSiteServices = () => useContext(SiteContext).services;
export const useSitePosts = () => useContext(SiteContext).posts;
export const useCompany = () => useContext(SiteContext).company;

// Company settings saved in the CMS override the baked-in defaults.
const mergeCompany = (base, o = {}) => ({
  ...base,
  name: o.companyName || base.name,
  founder: o.founderName || base.founder,
  phone: o.phone || base.phone,
  phoneHref: o.phone ? `tel:+1${String(o.phone).replace(/\D/g, "")}` : base.phoneHref,
  email: o.email !== undefined && o.email !== "" ? o.email : base.email,
  address: {
    ...base.address,
    street: o.addressStreet || base.address.street,
    suite: o.addressSuite || base.address.suite,
    city: o.addressCity || base.address.city,
    state: o.addressState || base.address.state,
    zip: o.addressZip || base.address.zip,
  },
  officeHours: {
    days: {
      en: o.hoursDaysEn || base.officeHours.days.en,
      es: o.hoursDaysEs || base.officeHours.days.es,
    },
    open: o.hoursOpen || base.officeHours.open,
    close: o.hoursClose || base.officeHours.close,
  },
  appointmentRequired: o.appointmentRequired !== undefined ? o.appointmentRequired : base.appointmentRequired,
  social: {
    instagram: o.instagram !== undefined ? o.instagram : base.social.instagram,
    facebook: o.facebook !== undefined ? o.facebook : base.social.facebook,
    tiktok: o.tiktok !== undefined ? o.tiktok : base.social.tiktok,
    linkedin: o.linkedin !== undefined ? o.linkedin : base.social.linkedin,
  },
});

export const SiteProvider = ({ lang, children }) => {
  const [status, setStatus] = useState(null);
  const [bundle, setBundle] = useState(null);
  const location = useLocation();
  const draftPreview = new URLSearchParams(location.search).get("preview") === "draft";

  const reloadStatus = useCallback(() => {
    return axios
      .get(`${API}/site/status`, { withCredentials: true })
      .then((r) => setStatus(r.data))
      .catch(() => setStatus({ mode: "public", previewActive: false, previewRequired: true }));
  }, []);

  useEffect(() => {
    reloadStatus();
  }, [reloadStatus]);

  useEffect(() => {
    axios
      .get(`${API}/content/bundle${draftPreview ? "?draft=1" : ""}`, { withCredentials: true })
      .then((r) => setBundle(r.data))
      .catch(() => setBundle({})); // graceful fallback to bundled defaults
  }, [draftPreview]);

  useEffect(() => {
    track("page_view", { path: location.pathname, lang });
  }, [location.pathname, lang]);

  const value = useMemo(() => {
    const t = (bundle && bundle[`lang:${lang}`]) || dictionaries[lang];
    const rawServices = (bundle && bundle.services) || SERVICES;
    // Inactive services (e.g. immigration) stay in the CMS but never surface publicly.
    const services = rawServices.filter((s) => s.status !== "inactive");
    // Immigration-category articles stay in the CMS but are not promoted publicly
    // while Immigration is an inactive service area.
    const posts = ((bundle && bundle.posts) || POSTS).filter((p) => p.category !== "immigration");
    const imageSlots = (bundle && bundle.imageSlots) || null;
    const seo = (bundle && bundle.seo) || {};
    const company = mergeCompany(COMPANY, (bundle && bundle.company) || {});
    return {
      t, services, posts, imageSlots, seo, company,
      status, reloadStatus, draftPreview,
      ready: bundle !== null && status !== null,
    };
  }, [bundle, status, lang, draftPreview, reloadStatus]);

  return <SiteContext.Provider value={value}>{children}</SiteContext.Provider>;
};
